import { useState, useRef, useEffect } from "react"
import { Palette, ChevronDown, Check, Sparkles, FileText, BookOpen, Plus } from "lucide-react"

// ============================================================================
// TYPES
// ============================================================================

export interface StyleGuideSource {
  type: "project" | "page" | "saved" | "extract"
  pageId?: string
  pageName?: string
  guideId?: string
  guideName?: string
}

interface PageInfo {
  id: string
  name: string
  slug: string
}

interface SavedStyleGuide {
  id: string
  name: string
}

interface StyleGuideBadgeProps {
  currentSource: StyleGuideSource
  onSourceChange: (source: StyleGuideSource) => void
  pages: PageInfo[]
  savedStyleGuides?: SavedStyleGuide[]
  currentPageName?: string
  onExtractFromCurrent?: () => void
  disabled?: boolean
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const StyleGuideBadge = ({
  currentSource,
  onSourceChange,
  pages,
  savedStyleGuides = [],
  currentPageName,
  onExtractFromCurrent,
  disabled = false,
}: StyleGuideBadgeProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Get display label based on current source
  const getDisplayLabel = (): string => {
    switch (currentSource.type) {
      case "project":
        return "Project Default"
      case "page":
        return currentSource.pageName || "Page Style"
      case "saved":
        return currentSource.guideName || "Saved Style"
      case "extract":
        return "Extracting..."
      default:
        return "Style Guide"
    }
  }

  // Get icon based on current source
  const getIcon = () => {
    switch (currentSource.type) {
      case "project":
        return <Palette className="w-3 h-3" />
      case "page":
        return <FileText className="w-3 h-3" />
      case "saved":
        return <BookOpen className="w-3 h-3" />
      default:
        return <Palette className="w-3 h-3" />
    }
  }

  const handleSelectProject = () => {
    onSourceChange({ type: "project" })
    setIsOpen(false)
  }

  const handleSelectPage = (page: PageInfo) => {
    onSourceChange({
      type: "page",
      pageId: page.id,
      pageName: page.name,
    })
    setIsOpen(false)
  }

  const handleSelectSaved = (guide: SavedStyleGuide) => {
    onSourceChange({
      type: "saved",
      guideId: guide.id,
      guideName: guide.name,
    })
    setIsOpen(false)
  }

  const handleExtract = () => {
    onExtractFromCurrent?.()
    setIsOpen(false)
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Badge Button */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          inline-flex items-center gap-1.5 px-2.5 py-1
          bg-gray-800 hover:bg-gray-700
          border border-gray-700
          rounded-lg text-xs font-medium
          transition-colors
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
          ${isOpen ? "bg-gray-700 border-gray-600" : ""}
        `}
      >
        {getIcon()}
        <span className="text-gray-300">{getDisplayLabel()}</span>
        <ChevronDown className={`w-3 h-3 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50">
          {/* Project Default */}
          <div className="p-1 border-b border-gray-800">
            <button
              onClick={handleSelectProject}
              className={`
                w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors
                ${
                  currentSource.type === "project"
                    ? "bg-primary/20 text-primary"
                    : "text-gray-300 hover:bg-gray-800 hover:text-white"
                }
              `}
            >
              <Palette className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-left">Project Default</span>
              {currentSource.type === "project" && <Check className="w-4 h-4" />}
            </button>
          </div>

          {/* Copy from Page */}
          {pages.length > 0 && (
            <div className="py-1 border-b border-gray-800">
              <div className="px-3 py-1.5 text-xs text-gray-500 font-medium">Copy from page</div>
              <div className="max-h-32 overflow-y-auto">
                {pages.map((page) => (
                  <button
                    key={page.id}
                    onClick={() => handleSelectPage(page)}
                    className={`
                      w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors
                      ${
                        currentSource.type === "page" && currentSource.pageId === page.id
                          ? "bg-primary/20 text-primary"
                          : "text-gray-300 hover:bg-gray-800 hover:text-white"
                      }
                    `}
                  >
                    <FileText className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1 text-left truncate">{page.name}</span>
                    {currentSource.type === "page" && currentSource.pageId === page.id && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Saved Style Guides */}
          {savedStyleGuides.length > 0 && (
            <div className="py-1 border-b border-gray-800">
              <div className="px-3 py-1.5 text-xs text-gray-500 font-medium">Saved style guides</div>
              <div className="max-h-32 overflow-y-auto">
                {savedStyleGuides.map((guide) => (
                  <button
                    key={guide.id}
                    onClick={() => handleSelectSaved(guide)}
                    className={`
                      w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors
                      ${
                        currentSource.type === "saved" && currentSource.guideId === guide.id
                          ? "bg-primary/20 text-primary"
                          : "text-gray-300 hover:bg-gray-800 hover:text-white"
                      }
                    `}
                  >
                    <BookOpen className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1 text-left truncate">{guide.name}</span>
                    {currentSource.type === "saved" && currentSource.guideId === guide.id && (
                      <Check className="w-4 h-4" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Extract from Current */}
          {onExtractFromCurrent && currentPageName && (
            <div className="p-1">
              <button
                onClick={handleExtract}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-purple-400 hover:bg-purple-500/10 transition-colors"
              >
                <Sparkles className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 text-left">Extract from {currentPageName}</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default StyleGuideBadge
